/**
 * Providers Admin & Analytics — Lot 19.
 */
import { useEffect, useState } from "react";
import {
  Loader2, RefreshCw, Wifi, WifiOff, AlertTriangle, TrendingUp,
  Zap, DollarSign, Clock, ArrowUpDown,
} from "lucide-react";
import api from "@/lib/api";

// ────────────────────────────────────────────────────────────────────────
// ProvidersAdminTab
// ────────────────────────────────────────────────────────────────────────

export const ProvidersAdminTab = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.platformListProviders();
      setProviders(d.providers || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const togglePriority = async (name, delta) => {
    const cur = providers.find((p) => p.name === name);
    const next = Math.max(1, (cur?.priority || 100) + delta);
    await api.platformPatchProvider(name, { priority: next });
    load();
  };
  const toggleEnabled = async (name, enabled) => {
    await api.platformPatchProvider(name, { enabled });
    load();
  };
  const resetHealth = async (name) => {
    await api.platformResetProviderHealth(name);
    load();
  };

  if (loading) return <Loader2 className="animate-spin" />;

  const grouped = providers.reduce((acc, p) => {
    (acc[p.channel] = acc[p.channel] || []).push(p);
    return acc;
  }, {});
  const CHANNEL_LABEL = { sms: "SMS", email: "Email", whatsapp: "WhatsApp", push: "Push" };

  return (
    <section className="space-y-6" data-testid="providers-admin-tab">
      {Object.entries(grouped).map(([channel, provs]) => (
        <div key={channel}>
          <p className="mb-3 text-[10.5px] font-semibold uppercase tracking-widest text-[#6B7280]">
            Canal {CHANNEL_LABEL[channel] || channel}
          </p>
          <div className="space-y-2.5">
            {provs.map((p) => (
              <ProviderRow key={p.name} p={p}
                            onTogglePriority={togglePriority}
                            onToggleEnabled={toggleEnabled}
                            onResetHealth={resetHealth} />
            ))}
          </div>
        </div>
      ))}
    </section>
  );
};

const HealthBadge = ({ status }) => {
  const cfg = {
    ONLINE:           ["bg-[#D9FFE8] text-[#166534] border-[#86EFAC]", Wifi],
    DEGRADED:         ["bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]", AlertTriangle],
    OFFLINE:          ["bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]", WifiOff],
    NOT_IMPLEMENTED:  ["bg-[#EDE9FE] text-[#5B21B6] border-[#C4B5FD]", Wifi],
  }[status] || ["bg-[#F1F5F9] text-[#475569]", Wifi];
  const [cls, Icon] = cfg;
  const label = status === "NOT_IMPLEMENTED" ? "NON IMPL." : status;
  return (
    <span data-testid={`health-${status}`}
          className={`inline-flex items-center gap-1 rounded-full border ${cls}
                      text-[10.5px] font-semibold px-2 py-0.5`}>
      <Icon size={10} strokeWidth={2.5} /> {label}
    </span>
  );
};

const ProviderRow = ({ p, onTogglePriority, onToggleEnabled, onResetHealth }) => (
  <div className="rounded-2xl border border-black/5 bg-white p-4"
       data-testid={`provider-row-${p.name}`}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="flex items-center gap-2">
          <p className="text-[14px] font-semibold text-[#111]">{p.name}</p>
          <HealthBadge status={p.status} />
          {!p.implemented && (
            <span className="text-[9.5px] uppercase tracking-widest bg-[#EDE9FE]
                            text-[#5B21B6] px-1.5 py-0.5 rounded">
              placeholder
            </span>
          )}
          {!p.enabled && (
            <span className="text-[9.5px] uppercase tracking-widest bg-[#F1F5F9]
                            text-[#475569] px-1.5 py-0.5 rounded">
              désactivé
            </span>
          )}
        </div>
        <p className="mt-1 text-[11px] text-[#6B7280]">
          Priorité {p.priority} · Coût est. {p.cost_estimate_eur.toFixed(3)}€ · 
          Uptime {p.uptime_percent}% ·
          Latence {p.avg_latency_ms}ms ·
          {p.countries.length ? `Pays: ${p.countries.join(", ")}` : "Tous pays"}
        </p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button onClick={() => onTogglePriority(p.name, -5)}
                title="Priorité haute (nombre plus faible)"
                data-testid={`priority-up-${p.name}`}
                className="h-8 w-8 rounded-full bg-[#F7F8FA] hover:bg-[#EEF2F5] flex items-center justify-center">
          <ArrowUpDown size={12} className="rotate-180" />
        </button>
        <button onClick={() => onTogglePriority(p.name, +5)}
                title="Priorité basse (nombre plus élevé)"
                data-testid={`priority-down-${p.name}`}
                className="h-8 w-8 rounded-full bg-[#F7F8FA] hover:bg-[#EEF2F5] flex items-center justify-center">
          <ArrowUpDown size={12} />
        </button>
        <button onClick={() => onResetHealth(p.name)}
                title="Reset health status"
                data-testid={`reset-health-${p.name}`}
                className="h-8 w-8 rounded-full bg-[#F7F8FA] hover:bg-[#EEF2F5] flex items-center justify-center">
          <RefreshCw size={12} />
        </button>
        <button onClick={() => onToggleEnabled(p.name, !p.enabled)}
                data-testid={`toggle-enabled-${p.name}`}
                className={`h-8 px-3 rounded-full text-[11.5px] font-semibold ${
                   p.enabled ? "bg-[#111] text-white" : "bg-[#F7F8FA] text-[#6B7280]"
                 }`}>
          {p.enabled ? "Actif" : "Inactif"}
        </button>
      </div>
    </div>
    <div className="mt-3 grid grid-cols-4 gap-2 text-center">
      <StatMini label="Succès"     value={p.total_successes} testId={`stat-success-${p.name}`} />
      <StatMini label="Échecs"     value={p.total_failures}  testId={`stat-fail-${p.name}`} />
      <StatMini label="Consécutifs" value={p.consecutive_failures} />
      <StatMini label="Fenêtre"    value={p.attempts_window} />
    </div>
  </div>
);

const StatMini = ({ label, value, testId }) => (
  <div className="rounded-lg bg-[#F7F8FA] py-1.5" data-testid={testId}>
    <p className="text-[13px] font-semibold text-[#111] leading-none">{value}</p>
    <p className="mt-0.5 text-[9.5px] uppercase tracking-widest text-[#6B7280]">{label}</p>
  </div>
);


// ────────────────────────────────────────────────────────────────────────
// AnalyticsTab
// ────────────────────────────────────────────────────────────────────────

export const AnalyticsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const d = await api.platformCommsAnalytics(days);
        setData(d);
      } finally { setLoading(false); }
    })();
  }, [days]);

  if (loading) return <Loader2 className="animate-spin" />;
  if (!data)   return <p>—</p>;

  return (
    <section className="space-y-6" data-testid="analytics-tab">
      <div className="flex items-center gap-2">
        {[7, 30, 90].map((n) => (
          <button key={n} onClick={() => setDays(n)}
                  data-testid={`period-${n}d`}
                  className={`h-8 rounded-full px-3 text-[11.5px] font-semibold ${
                     days === n ? "bg-[#111] text-white"
                                : "bg-white border border-black/5 text-[#6B7280]"
                   }`}>
            {n} jours
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <BigStat icon={Zap} label="Total envoyé"
                  value={data.total} testId="stat-total" />
        <BigStat icon={TrendingUp} label="Livrés"
                  value={data.delivered} sub={`${data.delivery_rate}%`}
                  testId="stat-delivered" tone="ok" />
        <BigStat icon={AlertTriangle} label="Échecs"
                  value={data.failed} sub={`${data.failure_rate}%`}
                  testId="stat-failed" tone="danger" />
        <BigStat icon={DollarSign} label="Coût total"
                  value={`${data.total_cost.amount.toFixed(2)}€`}
                  sub={`~${data.avg_cost.toFixed(3)}€/SMS`}
                  testId="stat-cost" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <BreakdownCard title="Par provider" data={data.by_provider}
                        testId="breakdown-provider" />
        <BreakdownCard title="Par événement" data={data.by_event}
                        testId="breakdown-event" />
        <BreakdownCard title="Par agence" data={data.by_agency}
                        testId="breakdown-agency" itemsAsCount />
      </div>

      <p className="text-[10.5px] text-[#6B7280]">
        Latence moyenne : {data.avg_latency_ms}ms ·
        Coût unitaire : {data.unit_cost_eur.toFixed(3)}€/segment
      </p>
    </section>
  );
};

const BigStat = ({ icon: Icon, label, value, sub, testId, tone }) => {
  const toneCls = {
    ok:     "bg-[#F3FFB0]/40 border-[#D8FF2B]/40",
    danger: "bg-[#FEE2E2]/50 border-[#FCA5A5]/40",
  }[tone] || "bg-white border-black/5";
  return (
    <div className={`rounded-2xl border ${toneCls} p-4`} data-testid={testId}>
      <div className="h-8 w-8 rounded-lg bg-[#111]/5 flex items-center justify-center">
        <Icon size={13} strokeWidth={2} className="text-[#111]" />
      </div>
      <p className="mt-3 text-[10.5px] font-semibold uppercase tracking-widest text-[#6B7280]">
        {label}
      </p>
      <p className="mt-1 text-[26px] font-semibold text-[#111] leading-none">{value}</p>
      {sub && <p className="mt-1 text-[11.5px] text-[#6B7280]">{sub}</p>}
    </div>
  );
};

const BreakdownCard = ({ title, data, testId, itemsAsCount }) => {
  const entries = Object.entries(data || {}).sort((a, b) => {
    const av = typeof a[1] === "object" ? a[1].count : a[1];
    const bv = typeof b[1] === "object" ? b[1].count : b[1];
    return bv - av;
  }).slice(0, 8);
  return (
    <div className="rounded-2xl border border-black/5 bg-white p-4" data-testid={testId}>
      <p className="text-[10.5px] font-semibold uppercase tracking-widest text-[#6B7280] mb-3">
        {title}
      </p>
      {entries.length === 0 ? (
        <p className="text-[12px] text-[#6B7280]">Aucune donnée</p>
      ) : (
        <div className="space-y-1.5">
          {entries.map(([k, v]) => {
            const count = itemsAsCount ? v : (typeof v === "object" ? v.count : v);
            return (
              <div key={k} className="flex items-center justify-between text-[12px]">
                <span className="text-[#374151] truncate max-w-[70%]">{k}</span>
                <span className="font-semibold text-[#111]">{count}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


// ────────────────────────────────────────────────────────────────────────
// AgencyCommsSettingsPage (route /app/parametres/communications)
// ────────────────────────────────────────────────────────────────────────

const TRIGGER_LABELS = {
  sms_reservation:  "Réservation confirmée",
  sms_dossier_cree: "Création du dossier",
  sms_documents:    "Documents disponibles",
  sms_financement:  "Financement validé",
  sms_preparation:  "Véhicule en préparation",
  sms_ct:           "Contrôle technique terminé",
  sms_revision:     "Révision terminée",
  sms_livraison_j1: "Livraison demain (J-1)",
  sms_livraison:    "Livraison confirmée",
  sms_carte_grise:  "Carte grise disponible",
  sms_cloture:      "Dossier clôturé",
};

const CHANNEL_LABELS = {
  sms:      "SMS transactionnels",
  email:    "Emails transactionnels",
  whatsapp: "WhatsApp (bientôt disponible)",
  push:     "Notifications push (bientôt disponible)",
};

export const AgencyCommsSettingsCard = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.agencyCommsSettings();
      setSettings(d);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleTrigger = async (key, enabled) => {
    setBusy(key);
    try {
      const d = await api.agencyToggleTrigger(key, enabled);
      setSettings((prev) => ({ ...prev, triggers: d.triggers, channels: d.channels }));
    } finally { setBusy(null); }
  };
  const toggleChannel = async (ch, enabled) => {
    setBusy(`ch:${ch}`);
    try {
      const d = await api.agencyToggleChannel(ch, enabled);
      setSettings((prev) => ({ ...prev, triggers: d.triggers, channels: d.channels }));
    } finally { setBusy(null); }
  };

  if (loading) return <Loader2 className="animate-spin" />;
  if (!settings) return null;

  return (
    <div className="space-y-6" data-testid="agency-comms-settings">
      {/* Channels */}
      <section className="rounded-2xl border border-black/5 bg-white p-5">
        <h3 className="text-[15px] font-semibold text-[#111]">Canaux de communication</h3>
        <p className="mt-1 text-[12.5px] text-[#6B7280]">
          Activez ou désactivez chaque canal indépendamment.
        </p>
        <div className="mt-4 space-y-2">
          {["sms", "email", "whatsapp", "push"].map((ch) => (
            <ToggleRow key={ch}
                       label={CHANNEL_LABELS[ch]}
                       enabled={settings.channels[ch] || false}
                       onChange={(v) => toggleChannel(ch, v)}
                       disabled={ch === "whatsapp" || ch === "push"}
                       busy={busy === `ch:${ch}`}
                       testId={`channel-toggle-${ch}`} />
          ))}
        </div>
      </section>

      {/* Triggers */}
      <section className="rounded-2xl border border-black/5 bg-white p-5">
        <h3 className="text-[15px] font-semibold text-[#111]">
          SMS transactionnels — 11 déclencheurs
        </h3>
        <p className="mt-1 text-[12.5px] text-[#6B7280]">
          Chaque événement du workflow peut envoyer automatiquement un SMS.
          Décochez pour désactiver l&apos;envoi automatique.
        </p>
        {!settings.channels.sms && (
          <div className="mt-3 rounded-lg bg-[#FEF3C7] text-[#92400E] px-3 py-2 text-[12px]">
            ⚠ Les SMS sont désactivés globalement (canal SMS off).
          </div>
        )}
        <div className="mt-4 space-y-1.5">
          {settings.available_triggers.map((key) => (
            <ToggleRow key={key}
                       label={TRIGGER_LABELS[key] || key}
                       enabled={settings.triggers[key] ?? true}
                       onChange={(v) => toggleTrigger(key, v)}
                       busy={busy === key}
                       testId={`trigger-toggle-${key}`} />
          ))}
        </div>
      </section>
    </div>
  );
};

const ToggleRow = ({ label, enabled, onChange, disabled, busy, testId }) => (
  <label className={`flex items-center justify-between rounded-xl px-3 py-2.5 border transition-colors
                     ${disabled ? "border-black/5 bg-[#F7F8FA] opacity-60"
                                : "border-black/5 hover:bg-[#F7F8FA] cursor-pointer"}`}
         data-testid={testId}>
    <span className="text-[13px] text-[#111]">{label}</span>
    <div className="flex items-center gap-2">
      {busy && <Loader2 size={12} className="animate-spin text-[#6B7280]" />}
      <input type="checkbox" checked={enabled} disabled={disabled || busy}
             onChange={(e) => onChange(e.target.checked)}
             className="h-4 w-4 accent-[#111]" />
    </div>
  </label>
);
